import type React from "react";

/**
 * 🔴 S3-07: ONE control for "Otevřít klienta", in all four places Servis offers
 * it.
 *
 * The same act was four different objects. In Provoz it was a `<button
 * class="text-button">` 36 px tall, in Tým a bare `<a>` with no class at all
 * (19 px), in Historii importů an `<a class="admin-import-client-link">` (17 px,
 * 12 px type) and in Zaseklých přepisech a proper 44 px button. On a phone the
 * reader aims at a seventeen-pixel line directly above a forty-four-pixel one
 * that does the same thing.
 *
 * Worse, two of the four were plain links with no handler: the click left the
 * single-page app entirely, so the console reloaded from scratch and the reader
 * lost the open drawer, the filters and their place in the list, and paid for
 * every admin request again. Round two fixed the height of this shape once, in
 * `styles.css`, but hung the fix on the `secondary-button` class - which these
 * three did not have.
 *
 * The href stays real, so the row is still a link a person can open in a new
 * tab or copy. `onOpenClient` is what keeps an ordinary click inside the app;
 * without it the link behaves as it always did, which is the honest fallback
 * for a surface that has no in-app navigation to offer.
 */
/**
 * The click behaviour on its own, for the two places where the link is a word
 * inside a dense feed row rather than a control in an action bar (the timeline
 * and the notification card). They keep their own small shape on purpose - a
 * 44 px button inside a three-line log row would be the loudest thing in it -
 * but they must not be the last paths that throw the whole console away.
 */
export function openClientHandler(
  clientId: string,
  onOpenClient?: (clientId: string) => void
): ((event: React.MouseEvent<HTMLAnchorElement>) => void) | undefined {
  if (!onOpenClient) return undefined;
  return (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    onOpenClient(clientId);
  };
}

export function OpenClientLink(props: {
  clientId: string;
  label: string;
  onOpenClient?: (clientId: string) => void;
  /** Opens the client on a specific staff step (the timeline's own idiom). */
  staffStep?: string;
}) {
  const href = `/?client=${encodeURIComponent(props.clientId)}${
    props.staffStep ? `&staffStep=${encodeURIComponent(props.staffStep)}` : ""
  }`;
  return (
    <a
      className="secondary-button compact-button open-client-link"
      href={href}
      onClick={(event) => {
        if (!props.onOpenClient) return;
        // A modified click is the reader asking for a second tab; let the
        // browser do its job instead of swallowing it.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        props.onOpenClient(props.clientId);
      }}
    >
      {props.label}
    </a>
  );
}
